// src/features/analytics/components/OverviewCards.tsx

import { Badge, Group, Paper, SimpleGrid, Skeleton, Text } from '@mantine/core';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import type { DatasetMeta, OverviewData } from '../types';

interface OverviewCardsProps {
  overview: OverviewData | null;
  isLoading: boolean;
}

const PLAYBACK_EMPTY_COPY = 'Playback analytics begins with the new player rollout.';
const NO_DATA_COPY = 'No data yet';

function hasDatasetData(meta: DatasetMeta | undefined): boolean {
  return !!meta?.coverage && meta.freshness?.state !== 'empty';
}

interface StatCardProps {
  label: string;
  value: string | null;
  /** Shown instead of a value when the dataset is absent — never a zero. */
  emptyCopy?: string;
  secondary?: React.ReactNode;
  isLoading: boolean;
}

function StatCard({ label, value, emptyCopy = NO_DATA_COPY, secondary, isLoading }: StatCardProps) {
  return (
    <Paper withBorder p="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      {isLoading ? (
        <Skeleton height={28} mt={6} width="60%" />
      ) : value !== null ? (
        <>
          <Text size="xl" fw={600} mt={2}>
            {value}
          </Text>
          {secondary}
        </>
      ) : (
        <Text size="sm" c="dimmed" mt={6}>
          {emptyCopy}
        </Text>
      )}
    </Paper>
  );
}

/**
 * Overview stat cards. Each card honors its own dataset meta independently —
 * playback empty while CDN has data is the expected Phase 5 production state.
 */
export function OverviewCards({ overview, isLoading }: OverviewCardsProps) {
  const playbackReady = !!overview?.playback && hasDatasetData(overview.datasets.playbackDaily);
  const playback = playbackReady ? overview.playback : undefined;

  const transferReady = !!overview?.transfer && hasDatasetData(overview.datasets.transfer);
  const transfer = transferReady ? overview.transfer : undefined;

  const concurrency = overview?.concurrency;
  const presenceReady = !!concurrency?.hasData && hasDatasetData(overview?.datasets.presence);

  // CDN delivery exposure rides the same flag as the delivery tab/route
  // (ADR-010 §3: never show two live bandwidth methodologies at once) —
  // transfer numbers stay dark on the Overview too until the flip. The value
  // arrives bundled in the overview response; only rendering is gated.
  const showDelivery = FEATURE_FLAGS.analyticsDeliveryEnabled;

  return (
    <SimpleGrid cols={{ base: 1, xs: 2, md: 3, xl: showDelivery ? 5 : 4 }} spacing="sm">
      <StatCard
        label="Sessions"
        value={playback ? AnalyticsUtils.formatCount(playback.sessions) : null}
        emptyCopy={PLAYBACK_EMPTY_COPY}
        isLoading={isLoading}
      />
      <StatCard
        label="Watch time"
        value={playback ? AnalyticsUtils.formatWatchTime(playback.watchTimeMs) : null}
        emptyCopy={PLAYBACK_EMPTY_COPY}
        isLoading={isLoading}
      />
      <StatCard
        label="Unique viewers"
        value={playback ? AnalyticsUtils.formatCount(playback.uniqueViewers) : null}
        emptyCopy={PLAYBACK_EMPTY_COPY}
        isLoading={isLoading}
      />
      {showDelivery && (
        <StatCard
          label="CDN delivery"
          value={transfer ? AnalyticsUtils.formatBytes(transfer.bytesTransferred) : null}
          secondary={
            transfer ? (
              <Text size="xs" c="dimmed" mt={2}>
                {AnalyticsUtils.formatBytes(transfer.finalizedBytes)} final ·{' '}
                {AnalyticsUtils.formatBytes(transfer.provisionalBytes)} provisional
              </Text>
            ) : undefined
          }
          isLoading={isLoading}
        />
      )}
      <StatCard
        label="Concurrent viewers"
        value={
          presenceReady && concurrency?.current !== undefined
            ? AnalyticsUtils.formatCount(concurrency.current)
            : null
        }
        secondary={
          presenceReady && concurrency?.peak !== undefined ? (
            <Group gap={6} mt={2}>
              <Badge variant="light" size="sm">
                Peak {AnalyticsUtils.formatCount(concurrency.peak)}
              </Badge>
            </Group>
          ) : undefined
        }
        isLoading={isLoading}
      />
    </SimpleGrid>
  );
}
